import Amplify, { API, graphqlOperation } from 'aws-amplify';
import aws_exports from '../aws-exports';
import {activityStatus, localesUnderscoreMap, recommendationModes} from '../constants/constants';
import {formatLocale_UpperCaseUnderscore} from "Services/locale";
import Timeout from 'await-timeout';
import {getUserData} from "Services/API";
import { getMockedSkillsDiagnosticData } from './__mocks__/StudentRecordService';
import { FEATURE_FLAGS } from '../constants/featureFlags';
import { createActivityQuery } from './SRSCreateAcitityQuery';
const request = require('superagent');
import { logSessionInfo } from '../services/logger';
import { GetBrandedAllowedCurriculums } from './BrandingService';
import { isProductionEnv, logSentryException, classifyRequestFailure } from './util';
import { graphQLRequestIAM } from './graphQLService';

const DEVONLY_SINGLE_PHRASE_STORY = false;

// SRS (Student Record Service) URL for IAM authenticated requests
const SRSurl = process.env.STUDENT_RECORD_STORE_URL;

// Constants for handling empty story recommendation responses
const EMPTY_RESULT_LIMIT_MULTIPLIER = 2; // Multiplier for increasing limit on empty response
const MAX_EMPTY_RESULT_RETRIES = 2; // Max retry attempts for empty responses

/**
 * App type enum for story recommendation filtering.
 * TUTOR: Student is in tutor mode without an assignment.
 * INSTRUCT: Student is in instruct mode with an assignment (non-assessment).
 */
export const APP_TYPE = {
  TUTOR: 'TUTOR',
  INSTRUCT: 'INSTRUCT'
};
Amplify.configure(aws_exports);

/***************************************************************************
*
* GRAPHQL QUERIES
*
****************************************************************************/
const GQL_PARENT_REPORT = `
query studentReport($studentId: String!, $equated: Boolean) {
  studentReport(studentId: $studentId, filter: {equatedScores: $equated}){
    parent{
      tips
      wcpm {
        score
        expected
      }
      AREA {
        score
        expected
      }
      vocabSize {
        score
        expected
      }
    }
  }
}`;

const GQL_DIAGNOSTIC_REPORT = `
query studentReport($studentId: String!){
  studentReport(studentId: $studentId){
    diagnosis{
      phon {
        id
        label
        score
        contents{
          name
          fields
        }
      }
      sight {
        id
        label
        score
        contents{
          name
          fields
        }
      }
      vocab {
        id
        label
        score
        contents{
          name
          fields
        }
      }
      decode {
        id
        label
        score
        contents{
          name
          fields
        }
      }
    }
  }
}`;

const GQL_ALL_REPORTS = `
query studentReport($studentId: String!){
  studentReport(studentId: $studentId){
    diagnosis{
      phon {
        id
        label
        contents{
          fields
        }
      }
      sight {
        id
        label
        contents{
          fields
        }
      }
      vocab {
        id
        label
        contents{
          fields
        }
      }
    }
  },
  diagnosis{
    phon {
      id
      label
      contents{
        fields
      }
    }
    sight {
      id
      label
      contents{
        fields
      }
    }
    vocab {
      id
      label
      contents{
        fields
      }
    }
  }
}`;

const GQL_OP_GETTAGS = `
query Activity($activityId: [String]!) {
  getActivity(activityId: $activityId) {
    tags
  }
}`;

// shallow query to fetch attributes of the child of an activity object
const GQL_OP_GETCHILD = `
query Activity($activityId: [String]!) {
  getActivity(activityId: $activityId) {
    children{
      activityId
      type
      status
    }
  }
}`;

const GQL_OP_GET_CHILD_ERRORS = `
query Activity($activityId: [String]!) {
  getActivity(activityId: $activityId) {
    children{
      activityId
      type
      status
      errors
    }
  }
}`;

const GQL_OP_GET_STATUS = `
query Activity($activityId: [String]!) {
  getActivity(activityId: $activityId) {
    status
    scores{
      wcpmScore
      equatedWCPM
    }
    scoreEstimates{
      ARM_estimate
      WCPM_estimate
    }
    errors
    flagging{
      triggered
    }
  }
}`;

const GQL_OP_ACTIVITY_ERRORS_BY_ID = `
query Activity($activityId: [String]!) {
  getActivity(activityId: $activityId) {
    errors
    flagging {
      triggered
    }
  }
}`;

// gets a complete story by id
const GQL_OP_STORY_BY_ID = `
query GetStory($storyId: String!,$locale: String) {
  getAmiraStoryById(storyid: $storyId) {
    storyid
    grade
    author
    title
    type,
    nonAudio,
    tags,
    referenceStoryId,
    referenceStoryPhrases,
    chapters(locale: $locale){
      phrases
      items{
        type
        assetURL
        videoURL
        imageURL
        writtenInstructions
        minPhraseTime
        timeoutInMS
        word
        targetGrapheme
        playFTUE
        canViewReferenceStory
        letterDisplay
        playComponentIntroductions
        questions{
          correct
          distractors
          feedbackMode
          readQuestion
          text
        }
        options {
          key
          imageUrl
        }
        interactions {
          ids
          prompt
          imageURL
          response
          noResponsePrompt
          textInput
          excerpts
          standardSet
          hints {
            prompt
            hint
          }
          standardMapping {
            standardSet
            standardId
            amiraSkillId
            description
            reason
          }
        }
        predictionQuestions{
          correct
          distractors
          text
        }
        introductions{
          textIntroductions{
            full
            short
          }
          verbalIntroductions{
            full
            short
          }
          imageURL
          type
        }
        instructions{
          verbalInstructions
          repeatable
          repeatVariation
          videoURL
          imageURL
          text
          word
          targetPhoneme
          type
          transition
          model
          example{
            originalWord
            targetWord
          }
        }
        wordPartToDelete
        version
        startingWord
      }
      overrides {
        phraseIndex
        wordIndex
        metadata {
          WORD
          PHON
          NPHON
          NSYL
          NLET
          CONC
          COGNATE {
            spanish
          }
          SIGHT
          AREA_AOA
          AREA_discrim
          IMG_URL
          VIDEO_URL
          RHYME
          GRAPHEMIC_BREAKDOWN
          DEFINITION{
            default
          }
          DEFINITION_VIDEO
          FUNFACTS_IMG_URL
          FUNFACTS_DESCRIPTION
          PHONEME_VIDEO_URL
          RIDDLE
          RIDDLE_ANSWER
          MORPHEME
          MORPHEME_MEANING
          MORPHEME_POSITION
          MORPHEME_ALTS
          NAME_MEANING
          NAME_PERSON
          NAME_NATIONALITY
          NAME_FLAG
          log_ngram_counts
        }
      }
    }
    questions {
      text
      distractors
      correct
    }
  }
}`;

// gets a list of stories
// for now this is being used in place of recommendations
const GET_STORIES = `
query Stories {
  stories {
    storyId
    title
    chapters {
      phrases
    }
    tags
    sequence_number
  }
}`;

const RECOMMEND = `
  query storyRecommendationService(
    $studentID: String,
    $studentGrade: String!,
    $storyType: String!,
    $numStoriesToReturn: Int!,
    $storyID: String,
    $mode: RecommendationMode
    $locale: StoryLocale
    $contentPackOverride: ContentPackOverride
    $appType: AppType
    $filterOnGradeLimits: Boolean){
    storyRecommendationService  (filter: {
    studentID: $studentID
    studentGrade: $studentGrade
    storyType: $storyType
    numStoriesToReturn: $numStoriesToReturn
    storyID: $storyID
    mode: $mode
    locale: $locale
    contentPackOverride: $contentPackOverride
    appType: $appType
    filterOnGradeLimits: $filterOnGradeLimits
    }){
      storyid
      title
      tags
      sequence_number
      AREA_85
      AREA_95
    }
  }`;

// returns a list of sparse activy objects
// to be used on teacher activities (student search) page
// eventually this will need to be integrated with SIS
const GQL_ACTIVITIES = `
query GetStory($limit: Int!) {
  activities(limit: $limit) {
    activityId
    studentId
    createdAt
    updatedAt
    score
    timeRead
    student {
      studentId
      lastName
      firstName
    }
    scores {
      areaScore
      esriScore
      phonAwareScore
      vocabSize
      wcpmScore
      equatedWCPM
    }
    status
  }
}`

const AUDIO_NETWORK_SIGNALS_FIELDS = `
      schemaVersion
      audioRuleVersion
      isLowVolume
      flatlineMaxRunSec
      badCount
      monitoringActive
      phrases {
        phraseIndex
        clippingP90
        ackP50Ms
        ackP95Ms
        ackedCount
      }
`;

// The `audioNetworkSignals` selection (added in TT-1256) is restored here now that the SRS field
// resolver is deployed. It was temporarily omitted under TT-1382 because the resolver threw
// `Lambda:Unhandled - Cannot find module 'audio-network-signals'` for every row, which made AppSync
// return a partial response with an `errors` array; graphQLRequestIAM (correctly) treats any GraphQL
// error as a failure and retries, so selecting this field forced every login studentActivities read
// through the full retry ladder. Do not re-omit without confirming the SRS resolver is undeployed.
const GQL_STUDENT_ACTIVITIES = `
query studentActivities($studentId: [String]!, $limit: Int, $activityType: [String]!, $status: [String], $equated: Boolean, $dateStart: String, $dateEnd: String, $displayStatus: [String], $locale: Locale) {
  studentActivities(studentId: $studentId, limit: $limit, filter: {activityType: $activityType, status: $status, equatedScores: $equated, dateStart: $dateStart, dateEnd: $dateEnd, displayStatus: $displayStatus, locale: $locale}) {
    activityId
    studentId
    assignmentId
    parentId
    audioNetworkSignals {
${AUDIO_NETWORK_SIGNALS_FIELDS}
    }
    children {
      activityId
      assignmentId
      parentId
      studentId
      type
      status
      displayStatus
      errors
      createdAt
      updatedAt
      tags
      story{
        tags
      }
      scoreEstimates {
        ARM_estimate
        WCPM_estimate
      }
      scores {
        areaScore
        esriScore
        phonAwareScore
        vocabSize
        wcpmScore
        equatedWCPM
        decodeScore
        armScore
        theta
      }
    }
    createdAt
    updatedAt
    timeRead
    score
    errors
    flagging {
      triggered
    }
    type
    scoreEstimates {
      ARM_estimate
      WCPM_estimate
    }
    student{
      studentId
      firstName
      lastName
    }
    scores {
      areaScore
      esriScore
      phonAwareScore
      vocabSize
      wcpmScore
      equatedWCPM
      decodeScore
      armScore
      theta
    }
    status
    tags
    displayStatus
    story {
      tags
      chapters {
        items {
          type
        }
      }
      grade
    }
  }
}`

// Login-time activity history (TT-991 / startup fetch consolidation). saveUserData used to issue
// five GQL_STUDENT_ACTIVITIES reads per login — each dragging `children`, `story.chapters.items`,
// `student` and the DynamoDB-backed audioNetworkSignals batch resolver for rows whose only use was
// `.length`. These two queries select exactly what saveUserData's consumers read
// (setV2InterventionUserParams, setUserTheta, the AREA/decode seed, the ERSS and partner checks,
// storiesRead/testsTaken, hasAnyCompletedActivity) and nothing that needs a sub-resolver.
// `storyId` is selected because SRS hands it back as hex and the tutor-history read needs it to
// drop the two ASR-check stories; `displayStatus` because "tutor completed but not yet scored" and
// "assessment downleveled to prereader" are displayStatus facts, not status ones.
//
// `equatedScores` is not what its name says. SRS implements it as `storyId IS NOT NULL` on the
// row query (amira-student-record-store src/srs/activity.js, listActivities) and forwards it to
// the Activity.scores field resolver (amira-scoring lambda-services/api/src/equate.py), which
// reports equatedWCPM as wcpmScore whenever equatedWCPM is non-null. The interventions walker has
// always read its rows through that flag, so the read that feeds it passes `equated: true`.
const LOGIN_ACTIVITY_FIELDS = `
    activityId
    storyId
    type
    status
    displayStatus
    createdAt
    scores {
      areaScore
      decodeScore
      equatedWCPM
      wcpmScore
      theta
    }
    scoreEstimates {
      ARM_estimate
      WCPM_estimate
    }
`;

const GQL_LOGIN_ACTIVITIES = `
query studentActivities($studentId: [String]!, $limit: Int, $activityType: [String]!, $status: [String], $equated: Boolean) {
  studentActivities(studentId: $studentId, limit: $limit, filter: {activityType: $activityType, status: $status, equatedScores: $equated}) {
${LOGIN_ACTIVITY_FIELDS}
  }
}`;

// Same row shape plus the TT-1256 payload, for the one read that classifies it. Kept as a separate
// document rather than a directive so the batch resolver is only invoked for rows that will be read.
const GQL_LOGIN_ACTIVITIES_WITH_SIGNALS = `
query studentActivities($studentId: [String]!, $limit: Int, $activityType: [String]!, $status: [String], $equated: Boolean) {
  studentActivities(studentId: $studentId, limit: $limit, filter: {activityType: $activityType, status: $status, equatedScores: $equated}) {
${LOGIN_ACTIVITY_FIELDS}
    audioNetworkSignals {
${AUDIO_NETWORK_SIGNALS_FIELDS}
    }
  }
}`;

const GQL_GET_TEST_METADATA = `
query studentActivities($studentId: [String]!, $limit: Int, $activityType: [String]!, $status: [String], $equated: Boolean) {
  studentActivities(studentId: $studentId, limit: $limit, filter: {activityType: $activityType, status: $status, equatedScores: $equated}) {
    activityId
    storyId
    createdAt
    updatedAt
    story{
      tags
    }
    status
    displayStatus
    tags
  }
}`

const GQL_PROGRESS_ACTIVITIES = `
query studentActivities($studentId: [String]!, $limit: Int, $activityType: [String]!, $status: [String], $equated: Boolean, $dateStart: String, $dateEnd: String) {
  studentActivities(studentId: $studentId, limit: $limit, filter: {activityType: $activityType, status: $status, equatedScores: $equated, dateStart: $dateStart, dateEnd: $dateEnd}) {
    activityId
    studentId
    story {
      title
      grade
      tags
    }
    createdAt
    updatedAt
    sessionTime
    score
    type
    errors
    tags
    displayStatus
    scores {
      areaScore
      esriScore
      phonAwareScore
      vocabSize
      wcpmScore
      equatedWCPM
      decodeScore
      armScore
      currentArmEstimate
    }
    scoreEstimates{
      ARM_estimate
      WCPM_estimate
    }
    children{
      type
      errors
      status
      displayStatus
      activityId
      scores {
        predWcpm
        armScore
      }
    }
    status
    comprehensionDialog {
      phraseIndex
    }
  }
}`

const GQL_CHECK_ASSIGNMENTS = `
query studentActivities($studentId: [String]!) {
  studentActivities(studentId: $studentId filter: {activityType: ["assessment","dyslexia","nwf","spelling","listeningComprehension","readingComprehension","vocabularyScreener"], status: ["assigned","scored","rescored","not_started","scoring","in_progress","underReview"]}, limit: 30) {
    activityId
    type
    status
    displayStatus
    createdAt
    parentId
    tags
    children{
      type
      activityId
      status
      displayStatus
      tags
    }
    story{
      tags
    }
  }
}`

const GQL_CHECK_SCORED = `
query studentActivities($studentId: [String]!) {
  studentActivities(studentId: $studentId filter: {activityType: ["assessment","tutor"], status: ["scored","rescored","not_started","scoring","in_progress","underReview"]}, limit: 30) {
    activityId
    storyId
    type
    status
    displayStatus
    tags
    children{
      type
      activityId
    }
  }
}`

const GQL_DYSLEXIA_ACTIVITIES = `
query studentActivities($studentId: [String]!, $limit: Int, $activityType: [String]!, $status: [String]) {
  studentActivities(studentId: $studentId, limit: $limit, filter: {activityType: $activityType, status: $status}) {
    activityId
    studentId
    createdAt
    status
    displayStatus
    tags
    scores{
      dyslexiaRiskCategory
      dyslexiaRiskIndicator
    }
  }
}`

const GQL_LAST_USER_THETA = `
query studentActivities($studentId: [String]!) {
  studentActivities(
    limit: 5
    filter: {activityType: ["assessment"], status: ["scored", "rescored", "not_started"], displayStatus: ["completed", "prereader"]}
    studentId: $studentId
  ) {
    scores {
      theta
    }
  }
}`

const GQL_GET_STUDENT_TREND = `
query getStudentTrend($studentTrendInput: StudentTrendInput!) {
  getStudentTrend(studentTrendInput: $studentTrendInput) {
    studentId
    trend
  }
}`;

// updates the error list of an activity and changes its status to rescored
const UPDATE_ACTIVITY = `
mutation UpdateActivity($activityId: String!, $sessionTime: Int, $errors: [[Boolean]], $score: Int, $skips: [Int], $selfCorrections: [Int], $mispronunciations: [Int],$timeRead: Int, $status: String, $displayStatus: String, $storyId: String, $parentId: String, $responses: [[String]],$tags: [ActivityTags], $phrasesRead: Int, $phraseCount: Int) {
  updateActivity(activityId: $activityId, sessionTime: $sessionTime, status: $status, displayStatus: $displayStatus, errors: $errors, score: $score, skips: $skips, selfCorrections: $selfCorrections, mispronunciations: $mispronunciations, timeRead: $timeRead, storyId: $storyId, parentId: $parentId, responses: $responses, tags: $tags, phrasesRead: $phrasesRead, phraseCount: $phraseCount) {
    activityId
    tags
  }
}`

// GT-327: the only document that names activeTime (SRS side: amira-student-record-store#741).
// UPDATE_ACTIVITY is shared by about 12 callers
// and SCORE_TUTOR is the terminal scoring write; GraphQL rejects an unknown argument even when the
// variable is omitted, so an SRS without activeTime would fail every caller of a document that
// names it. Keeping the argument here limits a wrong deploy order to saveSessionTime and the
// markInprogress heartbeat. The final activeTime reaches the row through saveSessionTime, which
// runs in logTimeOnStoryEnd before scoreTutoring.
const UPDATE_ACTIVITY_TIME = `
mutation UpdateActivityTime($activityId: String!, $sessionTime: Int, $activeTime: Int, $status: String, $phrasesRead: Int, $phraseCount: Int) {
  updateActivity(activityId: $activityId, sessionTime: $sessionTime, activeTime: $activeTime, status: $status, phrasesRead: $phrasesRead, phraseCount: $phraseCount) {
    activityId
    tags
  }
}`

const SCORE_TUTOR = `
mutation UpdateActivity($activityId: String!, $errors: [[Boolean]], $timeRead: Int, $status: String, $displayStatus: String, $interventionWords: [[Boolean]], $phrasesRead: Int, $phraseCount: Int) {
  updateActivity(activityId: $activityId, status: $status, displayStatus: $displayStatus, errors: $errors, timeRead: $timeRead, interventionWords: $interventionWords, phrasesRead: $phrasesRead, phraseCount: $phraseCount) {
    activityId
    tags
  }
}`

// The `audioNetworkSignals` response selection is restored now that the SRS field resolver is
// deployed. It was temporarily trimmed to `activityId` under TT-1382 because the resolver threw
// `Cannot find module 'audio-network-signals'` — every phrase-end write came back with an `errors`
// array and rejected even though the write itself landed, and ReaderManager's dedupe fingerprint
// (derived from the sent payload, not the response) never advanced, re-POSTing the full payload on
// every phrase end plus logging `error_saving_audio_network_signals`. Do not re-trim without
// confirming the SRS resolver is undeployed.
const UPDATE_AUDIO_NETWORK_SIGNALS = `
mutation UpdateActivity($activityId: String!, $audioNetworkSignals: ActivityAudioNetworkSignalsInput) {
  updateActivity(activityId: $activityId, audioNetworkSignals: $audioNetworkSignals) {
    activityId
    audioNetworkSignals {
${AUDIO_NETWORK_SIGNALS_FIELDS}
    }
  }
}`

const UPDATE_FLAG = `
mutation updatePhraseFlag($activityId: String!, $phraseIndex: Int, $triggered: Boolean){
  process(activityId: $activityId, phraseIndex: $phraseIndex, triggered: $triggered){
    triggered
  }
}`

const INITIAL_SCORE = `
mutation UpdateActivity($activityId: String!, $errors: [[Boolean]]!, $score: Int) {
  updateActivity(activityId: $activityId, status: "scored", errors: $errors, score: $score) {
    activityId
  }
}`;

// creates an activity with a type, an SIS student id, and a story id
const CREATE_ACTIVITY = `
mutation PutActivity($type: String!, $studentId: String!, $storyId: String!, $status: String, $displayStatus: String,$districtId: String, $schoolId: String, $tags: [ActivityTags], $assignmentId: String, $period: Int, $schoolYear: Int, $onCampus: Boolean) {
  putActivity(type: $type, studentId: $studentId, storyId: $storyId, status: $status, displayStatus: $displayStatus, districtId: $districtId, schoolId: $schoolId, tags: $tags, assignmentId: $assignmentId, period: $period, schoolYear: $schoolYear, onCampus: $onCampus) {
    activityId
    type
    status
    storyId
    districtId
    schoolId
    tags
    assignmentId
  }
}`;

const GQL_GET_LICENSING =`
query getLicensing($districtId: Int!, $schoolId: Int!){
  getAmiraLicenses(districtId: $districtId, schoolId: $schoolId) {
    audioDisabledDistricts
    tutorEnabled
    tutorAssignmentEnabled
    assessmentEnabled
    assessmentPageNavEnabled
    autoAssign
    branding
    partner
    calibrationEnabled
    demoMode
    demoLockdown
    dyslexiaEnabled
    dyslexiaParentLetterEnabled
    dyslexiaTestEnabled
    parentReportEnabled
    enablePretest
    prereaderAudioEnabled
    formOverrides{
      dyslexia
      nwf
      spelling
      listeningComprehension
      readingComprehension
      assessment
      vocabularyScreener
    }
    locale
    letternameConfig
    listeningCompConfig
    readingCompConfig
    reportingLanguage
    vocabularyScreenerConfig
    letterNamingAndSoundsConfig
    resourceProviders
    schoolYearStartDate
    screenerEnabled
    screeningStartDate
    screeningEndDate
    siteUnderMaintenance
    spanishConfig
    spellingConfig
    tutorWhitelist
    nwfConfig
    sorConfig{
      enableERPictures
      enablePracticePictures
      enableGiveWord
      enableFandP
      enableDRA
      enableLexile
      enableExtendedColumns
      selectedStandard
      interventionFrequency
      customARMLevelMapping
    }
    texasResources
    useStruggleMonitor
    useLatestARM
    iesEnabled
    districtAdminBadgingEnabled
    expandedEarlyReaderEnabled
    expandedInterventionSuppression
    newSpanishPlacementManagerEnabled
    badgingEnabled
    showBadNetworkResponse
    statePercentileCuts {
      state
      label
      cutscores {
        red
        yellow
        green
        blue
      }
    }
    voiceOverrides{
      locale
      override
    }
    windowDisabledByGrade
    curriculum {
      locale
      curriculumId
      displayName
      grades
    }
    automaticTextSetAssignments
    automaticInstructAssignments
    filterStoryOnGradeLimits
    midAssessmentAudioIssueTroubleshootEnabled
    fourCutlineCategoriesEnabled
    cursorFreeTutorWCPMThreshold
    amiraTutorSettings {
      tutorSelectionEnabled
      prohibitedRigs
      prohibitedSkins
    }
    # Keep in step with SCHOOL_PRODUCT_KEYS in App.js: the student product-access gate reads these keys.
    subscriptions {
      assess
      instruct
      tutor
      ensenar
      evaluar
      tutora
    }
    evaluarBTS
    enableBrowserASR
    enableTapToReadTutor
    badSignalCursorEnabled
    badSignalWcpmPivot
    badSignalMaxSignalAgeDays
    badSignalAudioThresholds
    badSignalNetworkThresholds
    forceBadAudioSignalOff
    forceBadNetworkSignalOff
    forceBrowserASROff
    forceTapWordOff
    forceCursorFreeOff
    forceNewSpanishASROff
    forceFilterStoryOnGradeLimitsOff
    allowedIpadBrowsers
    ignoreBrowserCompatibilityCheck
    assessmentTimerMultiplier
    amiraIsipVisibility
    state
    sensitivityTags
  }
}`;

const GQL_SKILL_FRAGMENT = `
  fragment skill on Skill {
    skillId
    name
    level
    resources {
      example {
        content
        resourceId
      }
      reference {
        url
        resourceId
        provider
        branding
        resourceIcon
        label
      }
    }
  }
`;

const GQL_SKILLGROUP_FRAGMENT = `
  fragment skillGroup on SkillGroup {
    percentileRank,
    LikelyMastered {
      ...skill
    }
    AppropriatelyChallenging {
      ...skill
    }
    VeryChallenging {
      ...skill
    }
  }
`;

const GQL_GET_DIAGNOSTIC_SKILLS = `
  query Skill($studentId: String!, $locale: Locale) {
    getSkills(studentId: $studentId, locale: $locale) {
      decoding(limit: 3) {
        ...skillGroup
      }
      vocabulary(limit: 3) {
        ...skillGroup
      }
      phonologicalAwareness(limit: 3) {
        ...skillGroup
      }
      sightRecognition(limit: 3) {
        ...skillGroup
      }
    }
  }
  ${GQL_SKILLGROUP_FRAGMENT}
  ${GQL_SKILL_FRAGMENT}
`;



const ENTITY_EXPORTS =`
query entityReports($entityId: String!, $entityType: String!){
  entityReports(entityId: $entityId, entityType: $entityType){
    entityId
    createdAt
    entityType
    status
  }
}`

const REQUEST_ENTITY_CSV_EXPORT =`
mutation exportEntityReport($entityId: String!, $entityType: String!){
  exportEntityReport(entityId: $entityId, entityType: $entityType){
    entityId
    createdAt
    entityType
    status
  }
}`

/*const onUpdateActivity = `subscription OnUpdateActivity($activityId: String) {
  onUpdateActivity(activityId: $activityId) {
    errors
  }
}
`;*/

export const onUpdateActivity = `
subscription OnUpdateActivity($activityId: String) {
  onUpdateActivity(activityId: $activityId) {
    errors
  }
}
`;


const CREATE_TUTOR_JOB = `
mutation createNrt($activityId: String!) {
  createNrtJob(activityId: $activityId) {
    activityId
    currentPass
    passes {
      model
      asr
      featureBatchFormat
      featureExtractor
      languageCode
      pipeline
      qualityAlgorithm
      scalingOverride
      slicingConfig
    }
    state
    updatedAt
    updatedBy
  }
}`

const GQL_LIST_CURRICULUMS = `
query GetListCurriculums {
  listCurriculums {
    curriculumId
    displayName
    locale
  }
}`;

const GQL_SKILLS_STATUS = `
query SkillsStatus($domain: Domain!, $classId: ID!, $nextToken: String, $locale: Locale, $curriculumId: String) {
  skillsStatus(classId: $classId,domain: $domain, limit: 50, nextToken: $nextToken, locale: $locale, curriculumId:$curriculumId
    ) {
    pageInfo {
      nextToken
    }
    records {
      studentId
      studentName
      domain
      mastery {
        value
        pacing
        grade
        month
      }
      progression {
        skillId
        skillName
        mastery {
          value
          pacing
          grade
          month
        }
        subSkills {
          skillId
          skillName
					skillAREAScore {
						AREA
						grade
						period
            month
				  }
					mastery {
						value
						pacing
						grade
						month
					}
          standard
          url
          exposureCount
          errorCount
        }
      }
    }
  }
}`;

const GQL_STUDENT_READING_ROPE = `
query StudentReadingRopeReport($studentId:ID, $locale: Locale, $useARM:Boolean, $useLatestARM:Boolean) {
  readingRopeReport(studentId:$studentId,locale:$locale,filter:{armScore: $useARM, useLatestScore: $useLatestARM}) {
    mastery {
      value
      pacing
      meta {
        srcActivityId
        srcActivityType
        srcGrade
        srcScoreName
        srcScoreValue
        srcSeason
      }
    }
    threads {
      label
      mastery {
        pacing
        value
      }
      domains {
        label
        mastery {
          value
          pacing
        }
        ... on StudentReadingRopeDomain {
          skills {
            skillId
            skillName
          }
        }
      }
    }
  }
}`;

const GQL_BATCH_STUDENT_READING_ROPE = `
query BatchStudentReadingRopeReport($studentIds: [ID], $locale: Locale, $useARM:Boolean, $useLatestARM:Boolean) {
  batchReadingRopeReport(studentIds: $studentIds, locale: $locale, filter: {armScore: $useARM, useLatestScore: $useLatestARM}) {
    studentId
    threads {
      domains {
        label
        ... on StudentReadingRopeDomain {
          label
          mastery {
            value
          }
        }
      }
      label
      mastery {
        value
      }
    }
  }
}`;

const GQL_CLASS_READING_ROPE = `
query ClassReadingRopeReport($classId:ID, $locale: Locale) {
  readingRopeReport(classId:$classId, locale: $locale) {
    mastery {
      value
      pacing
    }
    threads {
      mastery {
        value
        pacing
      }
      label
      domains {
        label
        mastery {
          value
          pacing
        }
      }
    }
  }
}`;

const GQL_SKILLS_DIAGNOSTIC_REPORT = `
query SkillsDiagnostic($studentId: ID!, $locale: Locale, $curriculumId: String) {
  diagnostic(studentId:$studentId, locale:$locale, curriculumId:$curriculumId, limit: 10000) {
    records {
        domain
        mastery {
          value
          pacing
          grade
          month
        }
        progression {
            skillId
            skillName
            skillArea
            url
            sequence
            mastery {
                value
                pacing
                grade
                month
            }
            subSkills {
              skillId
              skillName
              skillArea
              standard
              url
              sequence
              mastery {
                  value
                  pacing
                  grade
                  month
              }
              exposureCount
              errorCount
              standard
              skillAREAScore {
                AREA
                grade
                period
                month
              }
            }
        }
    }
  }
}`;

const GQL_RESOURCES = `
query Resources($studentId: ID!, $skillId: ID!) {
  skillResources(studentId: $studentId, skillId: $skillId) {
    resources {
      branding
      label
      provider
      resourceIcon
      resourceId
      url
    }
  }
}`;

const GQL_STUDENT_METRICS = `
  query StudentMetrics($studentIds: [String!]!, $dateStart: String, $dateEnd: String) {
    studentMetrics(studentId: $studentIds, dateStart: $dateStart, dateEnd: $dateEnd, activityType: ["tutor"]) {
      timeRead {
        sum
      }
      sessionTime {
        sum
      }
      activityCount(status: ["scored", "rescored"], activityType: ["tutor"]) {
        total
      }
      incompleteActivityCount: activityCount(status: ["in_progress", "not_started"], activityType: ["tutor"]) {
        total
      }
    }
  }
`

const SET_COMPREHENSION_DIALOG_FEEDBACK = `
  mutation SetComprehensionDialogFeedback($input: ComprehensionDialogFeedbackInput!) {
    setComprehensionDialogFeedback(input: $input) {
      activityId
      questionId
      stage
      feedback
      updatedAt
    }
  }
`;

const SET_COMPREHENSION_DIALOG_SCORE = `
  mutation SetComprehensionDialogScore($input: ComprehensionDialogScoreInput!) {
    setComprehensionDialogScore(input: $input) {
      activityId
      phraseIndex
      scoreOverride
    }
  }
`;


/***************************************************************************
*
* JS FUNCTIONS
*
****************************************************************************/

export default {
  async getStudentReport(studentId) {
    const data = await graphQLRequestIAM(SRSurl, GQL_DIAGNOSTIC_REPORT, { studentId });
    return data;
  },
  async getParentReport(studentId, equated) {
    const data = await graphQLRequestIAM(SRSurl, GQL_PARENT_REPORT, { studentId, equated });
    return data;
  },
  async getDiagnosticReport(studentId, locale) {
    locale = formatLocale_UpperCaseUnderscore(locale);
    const data = await graphQLRequestIAM(SRSurl, GQL_GET_DIAGNOSTIC_SKILLS, {studentId, locale});
    console.log("getDiagnostic",data); //leave in for debugging
    return data.data;
  },
  async scoreActivity(activityId, errors, skips, selfCorrections, mispronunciations,timeRead) {
    const displayStatus = activityStatus.RESCORED;
    const status = activityStatus.RESCORED;
    const data = await graphQLRequestIAM(SRSurl, UPDATE_ACTIVITY, { activityId, errors, skips, selfCorrections, mispronunciations, timeRead, displayStatus, status }, 3);
    console.log('DATA:', data);
    return data;
  },
  // phrasesRead / phraseCount (TT-1370 Partial Story Credit) are set on this terminal write so a
  // completed story carries phrasesRead === phraseCount. Callers pass both equal to the chapter's
  // phrase count. Optional: undefined values are left unwritten by the SRS resolver.
  async scoreTutoring(activityId,errors,timeRead,interventionWords,phrasesRead,phraseCount){
    const status = activityStatus.SCORED;
    const displayStatus = activityStatus.COMPLETED;
    const data = await graphQLRequestIAM(SRSurl, SCORE_TUTOR, {activityId, errors, timeRead, status, displayStatus, interventionWords, phrasesRead, phraseCount}, 3);
    console.log("scoreTutoring",data);
    return data;
  },
  async saveAudioNetworkSignals(activityId, audioNetworkSignals) {
    if (!activityId || !audioNetworkSignals) return null;
    // retries=0: phrase-end persist is incremental; do not stack retries on the tutor path.
    const data = await graphQLRequestIAM(SRSurl, UPDATE_AUDIO_NETWORK_SIGNALS, { activityId, audioNetworkSignals }, 0);
    console.log("saveAudioNetworkSignals", data);
    return data;
  },
  async setParent(activityId,parentId){
    if(!activityId || !parentId) return null;
    const data = await graphQLRequestIAM(SRSurl, UPDATE_ACTIVITY, {activityId, parentId});
    return data;
  },
  // activeTime (GT-327) is optional: the engagement clock's visible milliseconds.
  async saveSessionTime(activityId, sessionTime, activeTime){
    if(!activityId || !sessionTime) return null;
    const data = await graphQLRequestIAM(SRSurl, UPDATE_ACTIVITY_TIME, {activityId, sessionTime, activeTime}, 3);
    return data;
  },
  async getChild(activityId,parentId){
    const data = await graphQLRequestIAM(SRSurl, GQL_OP_GET_CHILD_ERRORS, {activityId});
    return data.data.getActivity[0].children;
  },
  async startActivity(activityId,storyId){
    //Don't set the status here.
    const data = await graphQLRequestIAM(SRSurl, UPDATE_ACTIVITY, {activityId, storyId}, 3);
    console.log(data);
    return data.data.updateActivity;
  },
  async markCompleted(activityId,displayStatus){
    let status = activityStatus.SCORING_NOT_STARTED;
    const data = await graphQLRequestIAM(SRSurl, UPDATE_ACTIVITY, {activityId, status, displayStatus}, 3);
    return data.data.updateActivity;
  },
  // phrasesRead / phraseCount (TT-1370 Partial Story Credit) are optional. They ride the
  // per-phrase tutor heartbeat so reporting can compute partial credit for abandoned stories.
  // Absent/undefined values are simply not written by the SRS resolver (nullable columns).
  //
  // retries=0 (was 3): phrasesRead is now monotonically meaningful, and this heartbeat fires
  // once per phrase, unawaited. With retries, a transient failure on an early phrase could
  // retry (backoff up to ~7s) and land AFTER a later phrase's write — or after the terminal
  // scoreTutoring write — overwriting a higher phrasesRead with a stale lower one via
  // UPDATE_ACTIVITY's plain set. Dropping retries removes that reordering window; the next
  // phrase's heartbeat re-sends fresh progress anyway. Mirrors saveAudioNetworkSignals, which
  // uses retries=0 for the same incremental-phrase-end reason.
  //
  // activeTime (GT-327) rides the same heartbeat. It only grows within a story, so the
  // retries=0 reordering argument above applies unchanged; the saveSessionTime write at story
  // end carries the final value.
  async markInprogress(activityId, phrasesRead, phraseCount, activeTime){
    let status = activityStatus.SCORING_IN_PROGRESS;
    const data = await graphQLRequestIAM(SRSurl, UPDATE_ACTIVITY_TIME, {activityId, status, phrasesRead, phraseCount, activeTime}, 0);
    return data.data.updateActivity;
  },
  async markAbortedKeepingAssignment(activityId,displayStatus=activityStatus.ABORTED){
    const existingTags = await this.getTags(activityId);
    let tags = [...new Set([...existingTags, "NO_QA", "NO_EDM", "NO_SCALING"])];
    let status = activityStatus.ASSIGNED;
    const data = await graphQLRequestIAM(SRSurl, UPDATE_ACTIVITY, {activityId, displayStatus, status});
    return data.data.updateActivity;
  },
  async markAborted(activityId,displayStatus=activityStatus.ABORTED){
    const existingTags = await this.getTags(activityId);
    let tags = [...new Set([...existingTags, "NO_QA", "NO_EDM", "NO_SCALING"])];
    let status = activityStatus.SCORING_NOT_STARTED;
    const data = await graphQLRequestIAM(SRSurl, UPDATE_ACTIVITY, {activityId, displayStatus, status, tags});
    return data.data.updateActivity;
  },
  async markArchived(activityId,displayStatus=activityStatus.ARCHIVED){
    let status = activityStatus.ARCHIVED;
    const data = await graphQLRequestIAM(SRSurl, UPDATE_ACTIVITY, {activityId, status, displayStatus}, 3);
    return data.data.updateActivity;
  },
  async updateStatus(activityId,displayStatus){
    if(!displayStatus) return Promise.reject(new Error("No display status provided to update"),{activityId,displayStatus});
    const data = await graphQLRequestIAM(SRSurl, UPDATE_ACTIVITY, {activityId, displayStatus}, 3);
    return data.data.updateActivity;
  },
  async primaryScore(activityId, errors, score) {
    const data = await graphQLRequestIAM(SRSurl, INITIAL_SCORE, { activityId, errors, score }, 3);
    console.log('DATA:', data);
    return data;
  },
  async finalizeNonAudioActivity(activityId, errors, responses) {
    let displayStatus = activityStatus.COMPLETED;
    const data = await graphQLRequestIAM(SRSurl, UPDATE_ACTIVITY, { activityId, errors, responses, displayStatus }, 3);
    console.log('DATA:', data);
    return data;
  },
  async saveTextResponses(activityId, responses) {
    const data = await graphQLRequestIAM(SRSurl, UPDATE_ACTIVITY, { activityId, responses}, 3);
    return data;
  },
  async activities(limit) {
    const data = await graphQLRequestIAM(SRSurl, GQL_ACTIVITIES, { limit }, 3);
    console.log('DATA:', data);
    return data.data.activities.filter((activity)=>{return activity && activity.scores && activity.scores.wcpmScore > 0});
  },
  // locale is a Locale enum value ("EN_US"/"ES_MX"). When unset the key is omitted from the variables
  // rather than sent as null, so SRS applies its own default and existing callers keep getting all locales.
  async studentActivities(
    studentId, 
    limit=1, 
    activityType=["assessment","tutor"], 
    status=["scored","rescored","assigned","not_started","in_progress","scoring"], 
    equated = false, 
    dateStart, 
    dateEnd, 
    displayStatus, 
    locale=null
  ) {
    try{
      const data = await graphQLRequestIAM(
        SRSurl, 
        GQL_STUDENT_ACTIVITIES, { 
          studentId, 
          limit, 
          activityType, 
          status, 
          equated, 
          dateStart, 
          dateEnd, 
          displayStatus, 
          ...(locale ? { locale } : {})
        }, 
        3
      );

      return data.data.studentActivities;
    }catch(err){
      console.log("Error Fetching one or more activities:", err.errors || err);
      return err.data?.studentActivities;
    }
  },
  /**
   * Light login-time read of a student's activity rows, newest first (SRS orders by createdAt desc
   * and applies `limit` after the status filter). `status` must be passed explicitly: SRS defaults
   * an absent filter to scored/rescored, which is the very filter TT-991 needs to widen. Mirrors
   * studentActivities' failure contract — returns whatever partial data the error carried, or
   * undefined — so callers treat "fetch failed" and "no rows" the same way they already do.
   *
   * @param {string|string[]} studentId
   * @param {Object} options
   * @param {number} options.limit
   * @param {string[]} options.activityType
   * @param {string[]} options.status
   * @param {boolean} [options.withSignals=false] select audioNetworkSignals (one DynamoDB batch per call)
   * @param {boolean} [options.equated=false] SRS equatedScores: storyId IS NOT NULL, and equatedWCPM reported as wcpmScore
   */
  async studentActivityHistory(studentId, { limit, activityType, status = [], withSignals = false, equated = false } = {}) {
    const query = withSignals ? GQL_LOGIN_ACTIVITIES_WITH_SIGNALS : GQL_LOGIN_ACTIVITIES;
    try {
      const data = await graphQLRequestIAM(SRSurl, query, { studentId, limit, activityType, status, equated }, 3);
      return data.data.studentActivities;
    } catch (err) {
      // Never rejects: login must not fail because history did. But one swallowed error here blanks
      // every login-time consumer at once (WCPM, ARM, theta, AREA/decode, ERSS, storiesRead), so it
      // is warned and written to the session log, where it can be counted, not console.log'd.
      console.warn('[studentActivityHistory] read failed', { limit, activityType, status, withSignals, equated }, err?.errors || err);
      logSessionInfo('student_activity_history_read_failed', {
        limit, activityType, status, withSignals, equated,
        error: err?.errors ? JSON.stringify(err.errors) : String(err?.message ?? err),
      });
      // Partial rows an errored response still carried (a per-row resolver error nulls that field
      // and keeps the row) are worth handing back. An errored response with an EMPTY list is not:
      // callers tell "read failed" from "nothing found" by whether they got an array, and the
      // saveUserData ERSS default must never run off an ambiguous empty, so that shape resolves
      // undefined, the same as a network failure.
      const partial = err?.data?.studentActivities;
      return Array.isArray(partial) && partial.length > 0 ? partial : undefined;
    }
  },
  async getTestMetadata(studentId, limit=1, activityType=["assessment","tutor"], status=["scored","rescored","assigned","not_started","in_progress","scoring"], equated = false) {
    const data = await graphQLRequestIAM(SRSurl, GQL_GET_TEST_METADATA, { studentId, limit, activityType, status, equated }, 3);
    return data.data.studentActivities;
  },
  async progressActivities(studentId, limit=1, activityType=["assessment","tutor"], equated = false, isARM = false, dateStart, dateEnd) {
    let status = ["scored","rescored","inProgress","underReview","scoring","reassess"];
    if (isARM) {
      // Include not_started activities when ARM is selected
      status.push("not_started");
    }
    let data = {};
    try{
      data = await graphQLRequestIAM(SRSurl, GQL_PROGRESS_ACTIVITIES, { studentId, limit, activityType, status, equated, dateStart, dateEnd });
    }catch(err){
      console.log('ERROR - while fetching progress activities: ',err);
      data = err;
    }

    //Put the arm estimate into the scores key of the activity so all null checks on tables/etc throughout the reporting don't have to add the scoreEstimates key
    if (data && data.data && data.data.studentActivities) {
      data.data.studentActivities.forEach(activity => {
          if (activity.scoreEstimates && activity.scores) {
              activity.scores.currentArmEstimate = activity.scoreEstimates.ARM_estimate;
          }
      });
    }
    return data.data.studentActivities;

  },
  async dyslexiaActivities(studentId, limit=1, status=["scored","rescored","underReview"]) {
    let activityType = 'dyslexia';
    const data = await graphQLRequestIAM(SRSurl, GQL_DYSLEXIA_ACTIVITIES, { studentId, limit, activityType, status });
    return data.data.studentActivities;
  },
  /**
   * Fetches the most recent theta score for a student from their completed assessment activities.
   * Returns the first non-null theta value from up to 5 recent scored/rescored assessments.
   * 
   * @param {string} studentId - The student's SIS ID
   * @returns {number|null} The most recent theta score, or null if none found
   */
  async getLastUserTheta(studentId) {
    try {
      const data = await graphQLRequestIAM(SRSurl, GQL_LAST_USER_THETA, { studentId });
      const activities = data.data.studentActivities;

      if (!activities || activities.length === 0) {
        return null;
      }

      const activityWithTheta = activities.find(
        activity => activity.scores && activity.scores.theta !== undefined && activity.scores.theta !== null
      );

      return activityWithTheta ? activityWithTheta.scores.theta : null;
    } catch (err) {
      console.log('Error fetching last user theta:', err);
      return null;
    }
  },
  async getStudentTrend(students, date, language) {
    const studentTrendInput = { students, date, language };
    const data = await graphQLRequestIAM(SRSurl, GQL_GET_STUDENT_TREND, { studentTrendInput });
    return data.data.getStudentTrend;
  },
  async getAssignedStory(studentId, activityType = ["assessment","dyslexia","nwf","spelling","listeningComprehension"], limit = 1){
    const status = [activityStatus.ASSIGNED];
    const data = await graphQLRequestIAM(SRSurl, GQL_STUDENT_ACTIVITIES, { studentId, limit, activityType, status });
    console.log("getAssigned:", data);
    return data.data.studentActivities;
  },
  async checkForAssignments(studentId){
    try {
      const data = await graphQLRequestIAM(SRSurl, GQL_CHECK_ASSIGNMENTS, {studentId});
      console.log("checkAssigned:", data);
      return data.data.studentActivities;
    }catch(err){
      console.log("Error fetching one or more activities:", err.errors || err);
      return err.data?.studentActivities;
    }
  },
  async checkForCompletedActivities(studentId){
    const data = await graphQLRequestIAM(SRSurl, GQL_CHECK_SCORED, {studentId}, 3);
    console.log("checkCompleted:", data);
    return data.data.studentActivities;
  },
  async getActivity(singleActivityId, excludeAudio = false) {
    let activityId = [];
    activityId.push(singleActivityId);
    const data = await graphQLRequestIAM(SRSurl, createActivityQuery(excludeAudio), { activityIds: activityId }, 3);
    console.log('DATA:', data);
    return data;
  },
  async getTags(singleActivityId){
    let activityId = [];
    activityId.push(singleActivityId);
    const data = await graphQLRequestIAM(SRSurl, GQL_OP_GETTAGS, { activityId }, 3);
    return data.data.getActivity[0].tags;
  },
  async getErrors(singleActivityId) {
    let activityId = [];
    activityId.push(singleActivityId);
    const data = await graphQLRequestIAM(SRSurl, GQL_OP_ACTIVITY_ERRORS_BY_ID, { activityId }, 3);
    console.log('Errors:', data);
    return data;
  },
  async getStory(storyId, locale = "en-us", retries = 2, backoff = 250) {

    // TODO: We can start writing more transformers, in a better spot, could help increase readability in places here
    // This transformer helps obfusicate some unecessary / unfavorably shaped data from the ingester.
    const transformComprehensionDialogData = (data) => {
      const userStandardSet = getUserData().sorConfig.selectedStandard;

      let foundIntroductionOverride = false;
      data.data.getAmiraStoryById.chapters = data.data.getAmiraStoryById.chapters.map(chapter => ({
        ...chapter,
        items: chapter.items.map(item => {
          if (item.type !== "interactive") {
            return item;
          }

          // The user should only see dialogs for their configured standard set, but the api gives all possible dialogs for this phrase and story
          // Filter the interactions array to include only those interactions
          // where at least one standardMapping has a standardSet matching userStandardSet.
          let filteredInteractions = item.interactions.filter(interaction =>
            interaction.standardMapping &&
            interaction.standardMapping.some(mapping => mapping.standardSet === userStandardSet)
          );
          console.log(`Comprehension Dialog: Found ${filteredInteractions.length} interactions for standard set '${userStandardSet}`);
          logSessionInfo('comprehension_dialogs_filtered', { filtered: filteredInteractions.map(i => i.ids[0]) });

          // This will only occur for the first interactive item.
          // The embedded introductions will be used for the start of the story introductions
          if (filteredInteractions.length && !foundIntroductionOverride && item.introductions) {
            chapter.items[0].introductions = item.introductions;
            delete item.introductions;
            foundIntroductionOverride = true;
          }

          // Transformations per interaction
          filteredInteractions = filteredInteractions.map(interaction => {
            const newInteraction = JSON.parse(JSON.stringify(interaction));
            // Parse the excerpts phrases from the indices sent.
            newInteraction.excerpts = interaction.excerpts.map(excerpt => data.data.getAmiraStoryById.chapters[0].phrases[excerpt]);

            return newInteraction;
          });

          return {
            ...item,
            interactions: filteredInteractions
          }
        })
      }));

      if (DEVONLY_SINGLE_PHRASE_STORY && !isProductionEnv()) {
        const short = data.data.getAmiraStoryById.chapters[0]
        // Shorten story
        short.phrases = short.phrases.map((p) =>  p.split(' ')[0] + ' ' + p.split(' ')[1])
        short.phrases = [
            short.phrases[0],
            short.phrases[1],
        ];
        short.items = [
            short.items[0],
            short.items[short.items.length - 1],
        ];
        // Shorten dialog
        if (short.items[short.items.length - 1].type === 'interactive') {
          // short.items[short.items.length - 1].interactions[0].prompt = 'Make it so';
          // short.items[short.items.length - 1].interactions[0].response = 'Make it just so';
          // short.items[short.items.length - 1].interactions[0].textInput = true;
        }
        if (short.items[0].introductions && short.items[0].introductions.verbalIntroductions && short.items[0].introductions.verbalIntroductions.full) {
          short.items[0].introductions.verbalIntroductions.full = "<speak>Skipping full intro.</speak>";
          short.items[0].introductions.verbalIntroductions.short = "<speak>Skipping short intro.</speak>";
        }
        console.log('%cShort story', '', storyId,locale,data);
        data.data.getAmiraStoryById.chapters = [short];
      }

      return data;
    }

    try {
      const data = await graphQLRequestIAM(SRSurl, GQL_OP_STORY_BY_ID, { storyId, locale }, 0);
      if (data.errors) {
        throw new Error(data.errors[0].message);
      }
      data.data.getAmiraStoryById.storyId = data.data.getAmiraStoryById.storyid;
      console.log('GetStory:', storyId,locale,data);
      const transformedData = transformComprehensionDialogData(data);

      return transformedData.data.getAmiraStoryById;
    } catch (err) {
      const errorMessage = err.message || err.errors?.[0]?.message || 'Unknown error';
      logSessionInfo('error_getting_story_by_id', { error: errorMessage, retriesRemaining: retries });
      console.log(`Error getting story by id.  Retries Remaining: ${retries}`, err);
      if (retries) {
        await new Promise(resolve => setTimeout(resolve, backoff));
        return this.getStory(storyId, locale, retries-1, backoff+250);
      } else {
        throw err;
      }
    }
  },
  async getStories() {
    const data = await graphQLRequestIAM(SRSurl, GET_STORIES, { }, 3);
    console.log('DATA:', data);
    return data;
  },
  /**
   * Internal helper to fetch story recommendations from the service.
   * Handles the actual GraphQL query and response mapping.
   * 
   * @private
   */
  async _fetchStoryRecommendations(studentID, studentGrade, storyType, numStoriesToReturn, storyID, mode, locale, contentPackOverride, appType, filterOnGradeLimits) {
    let finalGrade = studentGrade;
    if (locale === 'es-mx' && studentGrade < 0) {
      // We do not support PreK Spanish stories yet, so force grade K
      finalGrade = 0;
    }
    
    if (!mode) {
      mode = recommendationModes.SEQUENCE_EXPIRES;
    }
    locale = formatLocale_UpperCaseUnderscore(locale);
    
    const data = await graphQLRequestIAM(SRSurl, RECOMMEND, {
      studentID,
      studentGrade: finalGrade,
      storyType,
      numStoriesToReturn,
      storyID,
      mode,
      locale,
      contentPackOverride,
      appType,
      filterOnGradeLimits
    });
    
    const recommendations = data.data?.storyRecommendationService || [];
    return recommendations.map((story) => {
      story.storyId = story.storyid;
      return story;
    });
  },
  async getRecommendedStories(studentID,studentGrade,storyType,numStoriesToReturn,storyID="",mode,locale, contentPackOverride, appType, filterOnGradeLimits){
    const originalLimit = numStoriesToReturn;
    let currentLimit = numStoriesToReturn;
    let emptyResultRetryCount = 0;
    
    // Only apply empty-result retry logic if requesting more than 1 story
    const maxEmptyResultRetries = (originalLimit > 1) ? MAX_EMPTY_RESULT_RETRIES : 0;
    
    try {
      while (emptyResultRetryCount <= maxEmptyResultRetries) {
        const stories = await this._fetchStoryRecommendations(
          studentID,
          studentGrade,
          storyType,
          currentLimit,
          storyID,
          mode,
          locale,
          contentPackOverride,
          appType,
          filterOnGradeLimits
        );
        
        // If we got results or exhausted retries, return what we have (capped to original limit)
        if (stories.length > 0 || emptyResultRetryCount === maxEmptyResultRetries) {
          const cappedStories = stories.slice(0, originalLimit);
          const baseLogMetadata = {
            studentID,
            studentGrade,
            storyType,
            storyID,
            mode,
            locale,
            contentPackOverride,
            appType,
            filterOnGradeLimits,
            originalLimit,
            finalLimit: currentLimit,
            retriesUsed: emptyResultRetryCount,
            storiesFound: stories.length
          };
          
          if (cappedStories.length > 0) {
            logSessionInfo('story_recommendation_result', {
              ...baseLogMetadata,
              storiesReturned: cappedStories.length
            });
          } else {
            logSessionInfo('story_recommendation_empty', {
              ...baseLogMetadata
            });
          }
          return cappedStories;
        }
        
        // Empty result - retry with doubled limit
        emptyResultRetryCount++;
        currentLimit = currentLimit * EMPTY_RESULT_LIMIT_MULTIPLIER;
      }
      
      // Should not reach here, but return empty array as fallback
      return [];
    } catch (err) {
      const errorMessage = err.message || err.errors?.[0]?.message || 'Unknown error';
      logSessionInfo('error_in_recommend_query', { error: errorMessage, studentID, numStoriesToReturn });
      // GT-102: the fingerprint splits this into one Sentry issue per failure category instead of
      // grouping every failure mode under one constant message; the tag allows faceting inside each.
      // studentID and studentGrade are deliberately not sent - sanitizeDataForSentry does not strip either.
      const failureCategory = classifyRequestFailure(err);
      logSentryException(
        new Error('Story recommendation fetch failure'),
        'Error in StudentRecordService.getRecommendedStories',
        { originalError: errorMessage, originalStack: err?.stack, storyType, numStoriesToReturn },
        { tags: { failureCategory }, fingerprint: ['{{ default }}', failureCategory] }
      );
      throw err;
    }
  },
  async createActivity(type, studentId, storyId, status=activityStatus.SCORING_NOT_STARTED, displayStatus,districtId=null,schoolId=null, tags=[], assignmentId=null, period=null, schoolYear=null, onCampus=false) {
    try {
      // Use built-in retry mechanism with retries=3 (exponential backoff: 1s, 2s, 4s)
      const data = await graphQLRequestIAM(SRSurl, CREATE_ACTIVITY, { 
        type, 
        studentId, 
        storyId, 
        status, 
        displayStatus, 
        districtId, 
        schoolId, 
        tags, 
        assignmentId, 
        period, 
        schoolYear,
        onCampus
      }, 3);
      console.log('createActivity:', data);
      
      // Validate the response structure and ensure activityId is present.
      // A missing activityId causes downstream NonNull GraphQL errors (e.g. in updateErssStatus).
      if (!data || !data.data || !data.data.putActivity) {
        throw new Error('Invalid response structure: missing data, data.data, or data.data.putActivity');
      }
      if (!data.data.putActivity.activityId) {
        throw new Error('Invalid response: putActivity returned without an activityId');
      }
      
      return data;
    } catch (error) {
      // Log final failure after all retries exhausted
      logSessionInfo('put_activity_final_failure', {
        type,
        studentId, 
        storyId,
        error: error.message
      });
      throw error;
    }
  },
  async getEntitlements(districtId,schoolId){
    const data = await graphQLRequestIAM(SRSurl, GQL_GET_LICENSING, {districtId, schoolId}, 3);
    console.log("getEntitlements",data.data.getAmiraLicenses);
    return data.data.getAmiraLicenses;
  },
  async requestCSVExport(entityId,entityType){
    const data = await graphQLRequestIAM(SRSurl, REQUEST_ENTITY_CSV_EXPORT, {entityId, entityType});
    return data;
  },
  async getEntityReports(entityId,entityType){
    const data = await graphQLRequestIAM(SRSurl, ENTITY_EXPORTS, {entityId, entityType});
    return data;
  },
  async subscribeToErrorUpdates(activityId,callback=null){
    const subscription = await API.graphql(
      graphqlOperation(onUpdateActivity,{activityId: activityId})
    )

    subscription.subscribe({
      next: (resp) => {
        console.log("SUBSCRIPTION",Date.now(),resp,subscription); //TODO: Remove once happy with error detection
        if(resp && resp.value.data && resp.value.data.onUpdateActivity && resp.value.data.onUpdateActivity.errors){
          callback(resp.value.data.onUpdateActivity.errors);
        }else{
          callback(null);
        }
      },
      error: (err) => console.log(err)
    });
  },
  async updateFlaggedPhrase(activityId,phraseIndex,triggered){
    const data = await graphQLRequestIAM(SRSurl, UPDATE_FLAG, {activityId, phraseIndex, triggered}, 3);
    return data;
  },
  async createEPSJob(activityId) {
    const response = await graphQLRequestIAM(process.env.EPS_BASE_URL, CREATE_TUTOR_JOB, {activityId: activityId});
    return response.data;
  },
  async listCurriculums() {
    const response = await graphQLRequestIAM(process.env.SKILLS_ENDPOINT_URL, GQL_LIST_CURRICULUMS, {});
    const brandedAllowedCurriculums = GetBrandedAllowedCurriculums();
    response.data.listCurriculums = response.data.listCurriculums?.filter(c => brandedAllowedCurriculums.includes(c.curriculumId));
    return response.data;
  },
  async getSkillsStatusData({ classId, domain, nextToken="", locale, selectedCurriculum }) {
    const formattedLocale = locale && locale.includes("-") ? localesUnderscoreMap[locale] : locale;
    const variables = { classId, domain, nextToken, locale: formattedLocale, curriculumId: selectedCurriculum };
    const response = await graphQLRequestIAM(process.env.SKILLS_ENDPOINT_URL, GQL_SKILLS_STATUS, variables);
    return response.data;
  },
  async getSkillsDiagnosticData(studentId, locale, curriculumId) {
    const mockData = getMockedSkillsDiagnosticData(studentId);
    if(FEATURE_FLAGS.MOCK_ERSS_SKILLS_DIAGNOSTIC && mockData) {
      return mockData.data;
    }

    const formattedLocale = locale && locale.includes("-") ? localesUnderscoreMap[locale] : locale;
    const variables = {studentId, locale: formattedLocale, curriculumId}
    const response = await graphQLRequestIAM(process.env.SKILLS_ENDPOINT_URL, GQL_SKILLS_DIAGNOSTIC_REPORT, variables);
    return response.data;
  },
  async getReadingRopeData({ classId, studentId, studentIds, useARM = false, useLatestARM, locale }) {
    const formattedLocale = locale && locale.includes("-") ? localesUnderscoreMap[locale] : locale;
    const userSettingUseLatestArm = getUserData().useLatestARM;
    useLatestARM = useLatestARM === undefined ? userSettingUseLatestArm : useLatestARM;
    let query;
    let variables;
    if (classId) {
      query = GQL_CLASS_READING_ROPE
      variables = {classId, locale: formattedLocale}
    }
    if (studentId) {
      query = GQL_STUDENT_READING_ROPE
      variables = {studentId,useARM,useLatestARM,locale: formattedLocale}
    }
    if (studentIds) {
      query = GQL_BATCH_STUDENT_READING_ROPE
      variables = {studentIds, useARM, useLatestARM, locale: formattedLocale}
    }
    const response = await graphQLRequestIAM(process.env.SKILLS_ENDPOINT_URL, query, variables);
    return response.data;
  },
  async getResources(skillId,studentId) {
    const variables = {skillId,studentId}
    const response = await graphQLRequestIAM(process.env.SKILLS_ENDPOINT_URL, GQL_RESOURCES, variables);
    return response.data;
  },
  async getActivityStatusAndScores(singleActivityId) {
    let activityId = [];
    activityId.push(singleActivityId);
    const data = await graphQLRequestIAM(SRSurl, GQL_OP_GET_STATUS, { activityId });
    console.log('getStatus:', data);
    return data.data.getActivity[0];
  },
  async waitUntilScored(activityId,timeout){
    if(timeout > 0){
      let activity = await this.getActivityStatusAndScores(activityId);
      if(activity.status === 'scored'){
        return activity;
      }else{
        const sleepTimer = new Timeout();
        await sleepTimer.set(1000);
        return await this.waitUntilScored(activityId,timeout-1000);
      }
    }else{
      return null;
    }
  },
  /**
   * fetches student metrics
   * @param {[string]} studentIds
   * @param {string} dateStart YYYY-MM-DD, inclusive
   * @param {string} dateEnd YYYY-MM-DD, inclusive
   * @returns
   */
  async getStudentMetrics(studentIds, dateStart, dateEnd) {
    //Account for the fact that dateEnd isn't inclusive. This is convoluted to be sure becasue we want to use UTC dates
    let utcDateEnd = dateEnd.split('-');
    let adjustedDate = new Date(Date.UTC(utcDateEnd[0], utcDateEnd[1]-1, utcDateEnd[2]));
    adjustedDate.setDate(adjustedDate.getDate() + 1);
    dateEnd = adjustedDate.toISOString().split('T')[0];

    const response = await graphQLRequestIAM(SRSurl, GQL_STUDENT_METRICS, {studentIds, dateStart, dateEnd})
    return response.data.studentMetrics
  },
  async setComprehensionDialogFeedback(activityId, questionId, stage, feedback) {
    const data = await graphQLRequestIAM(SRSurl, SET_COMPREHENSION_DIALOG_FEEDBACK, { input: {activityId, questionId, stage, feedback} });
    console.log('setComprehensionDialogFeedback',data);
    return data.data.setComprehensionDialogFeedback;
  },
  async setComprehensionDialogScore(activityId, phraseIndex, newScore) {
    const data = await graphQLRequestIAM(SRSurl, SET_COMPREHENSION_DIALOG_SCORE, { input: {activityId, phraseIndex, score: newScore}});
    console.log('setComprehensionDialogScore', data);
    return data.data.setComprehensionDialogScore;
  }
};